<template>
  <div class="flex flex-row gap-5 justify-evenly">
    <div class="flex flex-col gap-5">
      <p class="text-xl font-bold">
        Retained Prior Draws: <span class="font-normal">{{ formatCurrency(retainage.retainagePriorDraws) }}</span>
      </p>
      <StackedLabelInputNumberNoForm
        v-model="retainage.retainageThisDraw"
        name="retainThisDraw"
        label="Retain This Draw:"
        :min="0"
        :stacked="false"
        mode="currency"
        classes="mr-2 font-bold text-xl"
        input-class="text-xl w-40"
        :disabled="!canEdit"
        @update:model-value="updatedRetainage"
      ></StackedLabelInputNumberNoForm>
      <p class="text-xl font-bold">
        Total Retained: <span class="font-normal">{{ formatCurrency(totalRetainage) }}</span>
      </p>
    </div>
    <div class="flex flex-col gap-5">
      <p class="text-xl font-bold">
        Released Prior Draws: <span class="font-normal">{{ formatCurrency(retainage.releasedPriorDraws) }}</span>
      </p>
      <StackedLabelInputNumberNoForm
        v-model="retainage.releasedThisDraw"
        name="releaseThisDraw"
        label="Release This Draw:"
        :min="0"
        :stacked="false"
        mode="currency"
        classes="mr-2 font-bold text-xl"
        input-class="text-xl w-40"
        :disabled="!canEdit"
        @update:model-value="updatedRetainage"
      ></StackedLabelInputNumberNoForm>
      <p class="text-xl font-bold">
        Total Released: <span class="font-normal">{{ formatCurrency(totalReleased) }}</span>
      </p>
    </div>
  </div>
  <Divider></Divider>
  <div class="flex flex-col my-6">
    <p class="text-2xl font-bold text-center">Retain Balance:</p>
    <p class="text-2xl font-bold text-center">{{ formatCurrency(retainageBalance) }}</p>
  </div>

  <SubmitOptions
    :allow-submit="canEdit"
    :submit-label="props.getRetainage ? 'Save' : 'Ok'"
    @cancelled="$emit('cancelled')"
    @submitted="submitRetainage"
  ></SubmitOptions>
</template>

<script setup lang="ts">
import { formatCurrency, getDrawBasedRetainage } from "@/services/Utility";
import type { Project } from "@/types/Projects";
import { type DrawBasedRetainage } from "@/types/Retainage";
import gsap from "gsap";

interface Props {
  project: Project;
  canEdit: boolean;
  getRetainage: boolean;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  submitted: [];
  cancelled: [];
}>();

const retainage = defineModel<DrawBasedRetainage>("retainage", { required: true });

const totalRetainage = computed<number>(() => retainage.value.retainagePriorDraws + retainage.value.retainageThisDraw);
const totalReleased = computed<number>(() => retainage.value.releasedPriorDraws + retainage.value.releasedThisDraw);
const retainageBalance = ref<number>(0);

const updatedRetainage = (): void => {
  setTimeout(() => {
    if (totalRetainage.value - totalReleased.value < 0) {
      retainage.value.releasedThisDraw = totalRetainage.value - retainage.value.releasedPriorDraws;
    }

    updateRetainageBalance();
  }, 10);
};

const updateRetainageBalance = (): void => {
  gsap.to(retainageBalance, {
    duration: 0.5,
    value: totalRetainage.value - totalReleased.value,
  });
};

const submitRetainage = (): void => {
  if (props.getRetainage) {
    axios
      .post("/retainage/update/draw-based/" + props.project.projectDrawInfo.drawId, retainage.value)
      .then((response) => {
        emit("submitted");
      })
      .catch((e) => errorApi(e));
  } else {
    emit("submitted");
  }
};

onBeforeMount(async (): Promise<void> => {
  if (props.getRetainage) {
    await getDrawBasedRetainage(props.project.projectDrawInfo.drawId).then((result: DrawBasedRetainage) => (retainage.value = result));
  }
  updateRetainageBalance();
});
</script>
